import { datadogLogs } from "@datadog/browser-logs";

import { createCaaClient } from "@repo/shopifyV2/graphql/createCaaClient";

import { SHOPIFY_DOMAIN } from "@const/urls";

import { refreshHikoCaaToken } from "@lib/hikoHeadlessSession";

import type { ICaaToken } from "@store/customer";
import { useCustomerStore } from "@store/customer";

/**
 * Customer Account API 전용 클라이언트.
 * jp locale에서만 사용 — 기존 `lib/graphql.ts`의 storefrontRequest(Storefront API)는 그대로 유지
 */

const JP_SHOP_DOMAIN = SHOPIFY_DOMAIN.jp.replace(/^https?:\/\//, "").replace(/\/$/, "");

const caaClient = createCaaClient<ICaaToken>({
  shopDomain: JP_SHOP_DOMAIN,
  clientId: import.meta.env.VITE_JP_CAA_CLIENT_ID as string,
  getToken: () => useCustomerStore.getState().caaToken,
  setToken: (caaToken) => {
    useCustomerStore.getState().loginCaa(caaToken);
  },
  onRefreshFailed: () => {
    useCustomerStore.getState().logout();
  },
  // "hiko" 세션은 refresh_token을 직접 보유하지 않으므로 위젯에 갱신을 위임
  refresh: (caaToken, refreshWithRefreshToken) =>
    caaToken.source === "hiko" ? refreshHikoCaaToken() : refreshWithRefreshToken(caaToken.refreshToken),
  onGraphQLErrors: (errors) => {
    errors.forEach(({ message, path }) => {
      datadogLogs.logger.error("[CAA] GraphQL error", { message, path: JSON.stringify(path) });
    });
  },
  onNetworkError: (_error, { status }) => {
    datadogLogs.logger.error("[CAA] Network error", { status });
  },
});

/**
 * 만료가 임박했으면 갱신한 뒤의 CAA 토큰을 반환한다.
 * Storefront API의 buyerIdentity에 jp 토큰을 실어보낼 때처럼, caaRequest 바깥에서도 유효한
 * access token이 필요한 곳에서 사용한다.
 */
export const getFreshCaaToken = caaClient.getFreshToken;

/** Customer Account API GraphQL 실행기 */
export const caaRequest = caaClient.request;

export default caaRequest;
